import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, ShieldCheck } from "lucide-react";
import { CONTACT, NAV, CERTS } from "../../data/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const TopStrip = () => (
  <div data-testid="cert-strip-banner" className="bg-mist text-navy border-b border-slate-200">
    <div className="mx-auto flex h-10 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 lg:px-10 text-xs">
      <div className="flex items-center gap-4 overflow-hidden">
        {CERTS.map((c) => (
          <span key={c.code} className="flex items-center gap-1.5 whitespace-nowrap font-medium tracking-wide">
            <ShieldCheck className="h-3.5 w-3.5 text-brand" /> {c.code} Certified
          </span>
        ))}
      </div>
      <div className="hidden md:flex items-center gap-5">
        <a data-testid="header-call-button" href={`tel:${CONTACT.phones[0].tel}`} className="flex items-center gap-1.5 hover:text-brand transition-colors">
          <Phone className="h-3.5 w-3.5" /> {CONTACT.phones[0].label}
        </a>
        <a data-testid="header-email-button" href={`mailto:${CONTACT.emails[0]}`} className="flex items-center gap-1.5 hover:text-brand transition-colors">
          <Mail className="h-3.5 w-3.5" /> {CONTACT.emails[0]}
        </a>
      </div>
    </div>
  </div>
);

const navCls = ({ isActive }) =>
  `relative py-2 text-[14px] font-medium transition-colors ${isActive ? "text-navy nav-active" : "text-slate-600 hover:text-navy"}`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
    <TopStrip />
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className={`border-b bg-white/95 backdrop-blur-md transition-shadow duration-300 ${scrolled ? "shadow-[0_6px_24px_-12px_rgba(15,20,40,0.25)] border-slate-200" : "border-slate-100"}`}>
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8 lg:px-10">
          <Link to="/" data-testid="header-logo-link" className="shrink-0">
            <img src="/img/logo.webp" alt="MU Plast" className="h-12 w-auto" />
          </Link>
          <nav className="hidden xl:flex items-center gap-8" data-testid="main-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={navCls} data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a data-testid="header-whatsapp-button" href={CONTACT.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="hidden sm:inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-[#25D366] hover:border-[#25D366] transition-colors">
              <WhatsAppIcon className="h-5 w-5" />
            </a>
            <span className="hidden sm:block"><Link to="/contact" data-testid="header-quote-button" className="btn bg-navy text-white hover:bg-navy-800 h-11">Request a quote</Link></span>
            <a href={`tel:${CONTACT.phones[0].tel}`} data-testid="header-mobile-call-button" aria-label="Call" className="sm:hidden inline-flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white">
              <Phone className="h-4 w-4" />
            </a>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu" className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-navy">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div data-testid="mobile-menu" className="xl:hidden border-t border-slate-100 bg-white animate-fade-down">
            <nav className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex flex-col">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) => `py-3.5 border-b border-slate-100 font-display text-lg ${isActive ? "text-brand" : "text-navy"}`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="grid grid-cols-2 gap-3 pt-5">
                <a href={`tel:${CONTACT.phones[0].tel}`} data-testid="mobile-menu-call" className="btn bg-navy text-white"><Phone className="h-4 w-4" /> Call</a>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-menu-whatsapp" className="btn bg-[#128C7E] text-white"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
};
